import Vue from 'vue'
import VueI18n from 'vue-i18n'

Vue.use(VueI18n)

const supportedLocales = ['en', 'es', 'pt']
const localeStorageKey = 'konvenio-locale'

const messages = {
  en: {
    common: {
      appName: 'Konvenio',
      back: 'Back',
      startFree: 'Request registration',
      or: 'or',
      managerAccess: 'Sign in',
      viewPricing: 'Pilot phase',
      backHome: 'Back to home',
      accept: 'Accept',
      reject: 'Reject',
      moreInfo: 'More information',
      refresh: 'Refresh',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      close: 'Close',
      create: 'Create',
      actions: 'Actions',
      date: 'Date',
      type: 'Type',
      status: 'Status',
      company: 'Company',
      companies: 'Companies',
      user: 'User',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      name: 'Name',
      lastName: 'Last name',
      document: 'Document',
      notes: 'Notes',
      contact: 'Contact',
      author: 'Author',
      access: 'Access',
      createdAt: 'Created',
      required: 'Required field',
      invalidEmail: 'Invalid email',
      minPassword: 'Minimum 6 characters',
      noName: 'No name',
      noCompany: 'No company',
      view: 'View',
      current: 'Current',
      confirmAction: 'Confirm action',
      unknownUser: 'User',
      unknownCompany: 'Company',
      loadError: 'The data could not be loaded.',
      updateError: 'The status could not be updated.'
    },
    appShell: {
      activeCompany: 'Active company',
      logout: 'Sign out',
      changePassword: 'Change password',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Confirm password',
      passwordChanged: 'Password updated successfully.',
      passwordChangeError: 'The password could not be updated.',
      deleteAccount: 'Delete my account',
      deleteTitle: 'Permanently delete account',
      deleteText: 'Your account, company request, company membership, personal data and associated files that can be located will be deleted. This action cannot be undone.',
      deleteConfirm: 'Delete permanently',
      deleteError: 'The account could not be deleted.',
      nav: {
        notices: 'Notices',
        agreement: 'Agreement',
        calendar: 'Work calendar',
        workers: 'Workers',
        suggestions: 'Suggestions',
        accessRequests: 'Access requests',
        managers: 'Managers'
      }
    },
    tutorial: {
      open: 'Help tour',
      close: 'Close tutorial',
      skip: 'Skip',
      previous: 'Previous',
      next: 'Next',
      finish: 'Finish',
      steps: {
        brand: {
          title: 'Your workspace',
          text: 'Here you can see the Konvenio logo and the company currently being managed.'
        },
        company: {
          title: 'Active company',
          text: 'If you manage several companies, use this selector to switch the data shown in the panel.'
        },
        nav: {
          title: 'Main sections',
          text: 'This menu opens each management area: notices, agreement documents, calendar and workers.'
        },
        noticesNav: {
          title: 'Notices',
          text: 'Use notices to publish internal messages that workers can read from the app.'
        },
        noticesCreate: {
          title: 'Create a board',
          text: 'This button creates a new notice board. Inside each board you can add individual notices.'
        },
        agreementNav: {
          title: 'Agreement',
          text: 'This section stores labor agreement documents, PDFs or editable text for the company.'
        },
        agreementCreate: {
          title: 'New document',
          text: 'Create a document, add a description, choose text or file, and notify workers when needed.'
        },
        calendarNav: {
          title: 'Work calendar',
          text: 'The calendar shows holidays, company days and yearly vacation or personal day settings.'
        },
        calendarAdd: {
          title: 'Calendar actions',
          text: 'Add new work years or open an existing year to define holidays and days off.'
        },
        workersNav: {
          title: 'Workers',
          text: 'Review employee access requests and manage who can see the company content in the app.'
        },
        workersTabs: {
          title: 'Worker status',
          text: 'These tabs separate pending, accepted and rejected requests so each action is easy to find.'
        },
        suggestionsNav: {
          title: 'Suggestions',
          text: 'Check feedback from workers or send ideas about the web panel from this section.'
        },
        suggestionsRefresh: {
          title: 'Refresh data',
          text: 'Use this button to reload the latest suggestions and check for new feedback.'
        },
        account: {
          title: 'Account actions',
          text: 'At the bottom you can change your password, sign out or delete the current account.'
        },
        help: {
          title: 'Repeat the tutorial',
          text: 'Use this button whenever you want to launch this guided tour again.'
        }
      }
    },
    landing: {
      heroTitle: 'Workforce management platform for companies',
      heroText: 'Konvenio makes it easier for workers to access workplace information. You manage it from the web and they check it from mobile.',
      // freeMonths: 'First 3 months free',
      appTitle: 'The app is for your company workers',
      appText: 'Employees check notices, agreements and calendars from mobile. You manage everything from a simple web panel.',
      googlePlayAlt: 'Get Konvenio on Google Play',
      iosWebButton: 'View on iOS',
      iosWebAlt: 'Open Konvenio web app for iOS',
      bulletInvite: 'Employee onboarding by invitation',
      bulletRoles: 'Role-based access (manager / employee)',
      bulletDocs: 'Notifications and centralized documentation',
      pricingTitle: 'Pilot phase',
      pricingTextBefore: 'Start free for',
      pricingTextStrong: '3 months',
      pricingTextAfter: 'After the pilot, we will review the next steps with your company.',
      pilotIntro: 'Konvenio is currently in pilot phase with selected companies.',
      pricingCaption: '* No card is required during the pilot. If payment conditions are introduced later, we will confirm them with you first.',
      freePeriodLabel: 'Free period:',
      freePeriodText: '3 months of full access for managers and employees',
      pilotMessageTitle: 'In pilot phase with early companies',
      pilotMessageText: 'We are opening access gradually to validate the platform with real teams. During this stage you can try Konvenio without adding a card; the commercial model and any future conditions will be defined later.',
      notePanel: 'Web panel for managers + Android app for employees',
      noteTools: 'Notices, digital agreements and work calendar',
      noteRoles: 'Company and role controls (each employee only sees their own content)',
      features: {
        noticesTitle: 'Notices',
        noticesText: 'Internal notifications for employees.',
        agreementsTitle: 'Digital agreements',
        agreementsText: 'Centralized access to workplace documentation.',
        calendarTitle: 'Calendar',
        calendarText: 'Work calendar, vacation and public holidays.',
        suggestionsTitle: 'Suggestions',
        suggestionsText: 'We take your suggestions into account.'
      },
      faqTitle: 'Frequently asked questions',
      faqSubtitle: 'The essentials before getting started.',
      faqs: [
        {
          question: 'What is included in the free period?',
          answer: 'It includes full access for 3 months: web panel for managers, employee app, notices, digital agreements and work calendar.'
        },
        {
          question: 'What happens after the pilot?',
          answer: 'At the end of the pilot, we will contact you to review the next steps. If the conditions do not suit you, access is disabled at no cost.'
        },
        {
          question: 'Does it cost employees anything?',
          answer: 'Employees use the app at no cost. The commercial model for companies is still being defined during the pilot.'
        },
        {
          question: 'Do I need an iPhone?',
          answer: 'The app is initially available on Android. Management is done from the web, so the manager does not depend on a mobile phone.'
        },
        {
          question: 'Will Konvenio be paid?',
          answer: 'The model is still being defined. During the pilot there is no card or automatic charge, and if payment conditions are introduced later, we will confirm them with you beforehand.'
        }
      ],
      footerTitle: 'Start without risk',
      footerTextBefore: 'Request access and try Konvenio',
      // footerTextStrong: '3 months free',
      footerTextAfter: '.'
    },
    login: {
      welcome: 'Welcome to Konven.io',
      email: 'Email',
      password: 'Password',
      enter: 'Sign in',
      google: 'Sign in with Google',
      forgot: 'Forgot your password?',
      contact: 'Contact:',
      missingCredentials: 'Enter your email and password.',
      missingResetEmail: 'Enter your email to recover your password.',
      passwordResetSent: 'We sent you an email to reset your password.',
      passwordResetError: 'Could not send the password reset email.',
      googleRegistered: 'User registered with Google successfully.',
      googleNoManagerAccess: 'This Google account is not registered as a manager. Request registration or sign in with an approved manager account.',
      noPermission: 'You do not have permission to access the web panel.',
      accessUnavailable: 'Access unavailable',
      passwordsMismatch: 'Passwords do not match.',
      registered: 'User registered successfully.'
    },
    authErrors: {
      default: 'Authentication could not be completed.',
      recentLoginRequired: 'For security, sign in again and delete the account right after.',
      adminRestricted: 'This operation is restricted by Firebase.',
      popupAlreadyOpen: 'A Google window is already open. Finish that attempt or try again.',
      emailInUse: 'An account with this email already exists.',
      invalidCredential: 'The email or password is not correct.',
      invalidEmail: 'The email address is not valid.',
      missingEmail: 'Enter your email address.',
      missingPassword: 'Enter your password.',
      networkFailed: 'Could not connect to Firebase. Check your connection and try again.',
      operationNotAllowed: 'This sign-in method is not enabled in Firebase.',
      popupBlocked: 'The browser blocked the Google window. Allow pop-ups to continue.',
      popupClosed: 'You closed the Google window before completing access.',
      tooManyRequests: 'Too many failed attempts. Wait a few minutes before trying again.',
      userDisabled: 'This account is disabled.',
      userNotFound: 'No user exists with this email.',
      weakPassword: 'The password must be at least 6 characters.',
      wrongPassword: 'The password is not correct.'
    },
    errors: {
      default: 'Something went wrong. Please try again.',
      network: 'Could not connect. Check your internet connection and try again.',
      permissionDenied: 'You do not have permission to perform this action.',
      unauthenticated: 'Sign in again to continue.',
      notFound: 'The requested information could not be found.',
      fileNotFound: 'The file could not be found.',
      timeout: 'The operation took too long. Please try again.',
      serviceUnavailable: 'The service is temporarily unavailable. Please try again in a few minutes.',
      resourceExhausted: 'The service is temporarily overloaded. Please try again later.',
      failedPrecondition: 'The operation cannot be completed with the current data.',
      cancelled: 'The operation was cancelled.',
      adminRestricted: 'This operation is restricted by Firebase.',
      popupAlreadyOpen: 'A Google window is already open. Finish that attempt or try again.',
      emailInUse: 'An account with this email already exists.',
      invalidCredential: 'The email or password is not correct.',
      invalidEmail: 'The email address is not valid.',
      missingEmail: 'Enter your email address.',
      missingPassword: 'Enter your password.',
      operationNotAllowed: 'This sign-in method is not enabled.',
      popupBlocked: 'The browser blocked the Google window. Allow pop-ups to continue.',
      popupClosed: 'You closed the Google window before completing access.',
      recentLoginRequired: 'For security, sign in again and try the action right after.',
      tooManyRequests: 'Too many failed attempts. Wait a few minutes before trying again.',
      userDisabled: 'This account is disabled.',
      userNotFound: 'No user exists with this email.',
      weakPassword: 'The password must be at least 6 characters.',
      wrongPassword: 'The password is not correct.'
    },
    requestAccess: {
      title: 'Request access',
      subtitle: 'Access for company managers and works councils. Workers register from the app.',
      reviewBefore: 'We review requests within approximately',
      reviewStrong: '24-48 business hours',
      reviewAfter: 'You will receive the answer at the email address provided.',
      name: 'Full name',
      email: 'Email',
      company: 'Company',
      address: 'Company address',
      phone: 'Phone',
      accessMethod: 'How would you like to access?',
      accessMethodGoogle: 'With Google',
      accessMethodPassword: 'With username and password',
      accessMethodShortGoogle: 'Google',
      accessMethodShortPassword: 'Username and password',
      notes: 'Additional comments',
      privacyLink: 'Privacy Policy',
      legalNoticeLink: 'Legal Notice',
      cookiesLink: 'Cookie Policy',
      legalAcceptPrefix: 'I accept the legal terms:',
      legalAcceptAnd: 'and the',
      submit: 'Send request',
      successBefore: 'Request sent successfully.',
      successAfter: 'We will reply by email as soon as it is reviewed.',
      question: 'Any questions?',
      writeUs: 'Write to us at',
      required: 'Required field',
      invalidEmail: 'Invalid email',
      acceptedRequired: 'You must accept data processing',
      submitError: 'Error sending the request. Please try again.'
    },
    cookies: {
      message: 'We use analytics cookies to improve the experience. You can accept or reject their use.',
      analyticsEnabled: 'Cookies accepted: Analytics enabled'
    },
    statuses: {
      pending: 'Pending',
      reviewed: 'Reviewed',
      accepted: 'Accepted',
      rejected: 'Rejected'
    },
    admin: {
      accessRequests: {
        title: 'Access requests',
        empty: 'No access requests.',
        documentTitle: 'View document',
        updated: 'Status updated.',
        loadError: 'The requests could not be loaded.',
        updateError: 'The status could not be updated.'
      },
      managers: {
        title: 'Manager management',
        companyFormTitle: 'Create company',
        managerFormTitle: 'Create manager',
        companyName: 'Company name',
        contactEmail: 'Contact email',
        temporaryPassword: 'Temporary password',
        password: 'Password',
        googlePending: 'Google pending',
        passwordGoogle: 'password/google',
        companyManagers: 'Company managers',
        backToCompanies: 'Back to companies',
        emptyManagers: 'No managers for this company.',
        createdCompany: 'Company created successfully.',
        createdManager: 'Manager created successfully.',
        createdManagerWithVerification: 'Manager created successfully. A verification email has been sent to confirm the email address.',
        promotedExistingManager: 'Existing user promoted to manager. They will keep their current sign-in method.',
        deletedCompany: 'Company deleted successfully.',
        deletedManager: 'Manager deleted successfully.',
        companyCreateError: 'The company could not be created.',
        companyDeleteError: 'The company could not be deleted.',
        managerCreateError: 'The manager could not be created.',
        managerDeleteError: 'The manager could not be deleted.',
        deleteCompanyConfirm: 'Delete company "{name}"?',
        deleteCompanyWarning: 'This will delete the company, notices, work calendars, worker links and manager access for this company. This action cannot be undone.',
        deleteConfirm: 'Delete manager "{name}"?',
        deleteLastManagerWarning: 'This is the only manager for this company. If you delete it, the company will have no managers with web access.',
        selectValidCompany: 'Select a valid company.',
        loadError: 'The data could not be loaded.'
      }
    },
    workers: {
      title: 'Worker management',
      pending: 'Pending',
      accepted: 'Accepted',
      rejected: 'Rejected',
      searchPlaceholder: 'Search by name or email',
      emptySearch: 'No workers match your search',
      emptyPending: 'No pending requests',
      emptyAccepted: 'No workers',
      emptyRejected: 'No rejected requests',
      acceptedNotificationTitle: 'Request accepted',
      acceptedNotificationBody: 'You now have access to {company}.',
      rejectedNotificationTitle: 'Request rejected',
      rejectedNotificationBody: 'Your request for {company} has been rejected.',
      yourCompany: 'your company',
      theCompany: 'the company',
      loadError: 'The workers could not be loaded.',
      updateError: 'The worker request could not be updated.',
      acceptError: 'The request could not be accepted.',
      rejectError: 'The request could not be rejected.',
      revokeError: 'The worker access could not be revoked.',
      restoreError: 'The request could not be restored.',
      acceptedNotificationError: 'The acceptance notification could not be sent.',
      rejectedNotificationError: 'The rejection notification could not be sent.'
    },
    notices: {
      title: 'Notices',
      newNotice: 'New notice',
      editNotice: 'Edit notice',
      noticeTitle: 'Notice title',
      description: 'Description',
      saveNotice: 'Save notice',
      seeMore: 'See more',
      emptyBoard: 'This board has no notices yet.',
      newBoard: 'New board',
      editBoard: 'Edit board',
      boardTitle: 'Board title',
      deleteBoardConfirm: 'Delete this board and all its notices?',
      deleteNoticeConfirm: 'Delete this notice?',
      notificationTitle: 'New notice',
      notificationError: 'The notice notification could not be sent:',
      loadError: 'The notices could not be loaded.',
      saveBoardError: 'The board could not be saved.',
      deleteBoardError: 'The board could not be deleted.',
      saveNoticeError: 'The notice could not be saved.',
      deleteNoticeError: 'The notice could not be deleted.'
    },
    agreement: {
      title: 'Agreement',
      newDocument: 'New document',
      editDocument: 'Edit document',
      documentTitle: 'Title',
      description: 'Description',
      maxDescription: 'Maximum 500 characters',
      documentType: 'Document type',
      editableText: 'Editable text',
      pdfDocument: 'PDF document',
      currentDocument: 'Current document:',
      replaceFile: 'Replace file (optional)',
      uploadFile: 'Upload file',
      saveChanges: 'Save changes',
      createDocument: 'Create document',
      viewDocument: 'View document',
      viewText: 'View text',
      deleteConfirm: 'Delete "{title}"?',
      notificationTitle: 'New agreement document',
      notificationError: 'The agreement notification could not be sent:',
      loadError: 'The agreement documents could not be loaded.',
      saveError: 'The agreement document could not be saved.',
      deleteError: 'The agreement document could not be deleted.'
    },
    calendar: {
      title: 'Work calendars',
      addYear: 'Add new work year',
      workYear: 'Work year {year}',
      addHoliday: 'Add non-working day',
      editHoliday: 'Edit non-working day',
      vacation: 'Vacation',
      personalDays: 'Personal days',
      national: 'National',
      regional: 'Regional',
      company: 'Company',
      holidaysTitle: 'Non-working days {year}',
      reason: 'Reason',
      addRow: 'Add row',
      completeRows: 'Complete every row before saving.',
      emptyHolidays: 'No non-working days defined.',
      deleteTitle: 'Delete holiday',
      deleteConfirm: 'Are you sure you want to delete this non-working day?',
      loadError: 'The work calendars could not be loaded.',
      saveError: 'The work calendar could not be saved.',
      deleteError: 'The non-working day could not be deleted.'
    },
    suggestions: {
      title: 'Suggestions',
      appTab: 'App suggestions',
      platformTab: 'Platform suggestions',
      companiesTab: 'Company suggestions',
      agreementTitle: 'Agreement suggestions',
      platformTitle: 'Platform suggestion',
      platformIntro: 'Send ideas, improvements or any contribution about the Konvenio web panel.',
      placeholder: 'Write your suggestion here',
      send: 'Send suggestion',
      emptyCategory: 'No suggestions in this category.',
      emptyAgreement: 'No agreement suggestions for your company.',
      loadError: 'The suggestions could not be loaded.',
      missingText: 'Please write a suggestion before sending it.',
      missingCompany: 'The company name could not be found.',
      thanks: 'Thanks for your contribution.',
      saveError: 'There was a problem saving the suggestion.',
      viewed: 'Viewed',
      notViewed: 'Not viewed',
      markViewed: 'Mark as viewed',
      markViewedSuccess: 'Suggestion marked as viewed.',
      markViewedError: 'The suggestion could not be marked as viewed.',
      types: {
        agreement: 'Agreement',
        app: 'App',
        platform: 'Platform'
      }
    },
    info: {
      legalTitle: 'Legal Notice',
      privacyTitle: 'Privacy Policy',
      cookiesTitle: 'Cookie Policy',
      intro: 'This page contains the legal information for Konvenio.',
      owner: 'Website owner',
      purpose: 'Purpose',
      dataProtection: 'Data protection',
      cookies: 'Cookies',
      contact: 'Contact',
      country: 'Country',
      spain: 'Spain',
      rights: 'Rights',
      rightsText: 'Users may exercise their access, rectification, erasure, restriction, objection and portability rights by contacting Konvenio.',
      dataCategories: 'Data categories',
      processingPurposes: 'Processing purposes',
      retention: 'Data retention',
      providers: 'Third-party providers',
      moreInfo: 'More information'
    }
  },
  es: {
    common: {
      appName: 'Konvenio',
      back: 'Volver atrás',
      startFree: 'Solicitar registro',
      or: 'o',
      managerAccess: 'Iniciar sesión',
      viewPricing: 'Fase piloto',
      backHome: 'Volver al inicio',
      accept: 'Aceptar',
      reject: 'Rechazar',
      moreInfo: 'Más información',
      refresh: 'Actualizar',
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      edit: 'Editar',
      close: 'Cerrar',
      create: 'Crear',
      actions: 'Acciones',
      date: 'Fecha',
      type: 'Tipo',
      status: 'Estado',
      company: 'Empresa',
      companies: 'Empresas',
      user: 'Usuario',
      email: 'Email',
      phone: 'Teléfono',
      address: 'Dirección',
      name: 'Nombre',
      lastName: 'Apellidos',
      document: 'Documento',
      notes: 'Notas',
      contact: 'Contacto',
      author: 'Autor',
      access: 'Acceso',
      createdAt: 'Alta',
      required: 'Campo obligatorio',
      invalidEmail: 'Email no válido',
      minPassword: 'Mínimo 6 caracteres',
      noName: 'Sin nombre',
      noCompany: 'Sin empresa',
      view: 'Ver',
      current: 'Actual',
      confirmAction: 'Confirmar acción',
      unknownUser: 'Usuario',
      unknownCompany: 'Empresa',
      loadError: 'No se pudieron cargar los datos.',
      updateError: 'No se pudo actualizar el estado.'
    },
    appShell: {
      activeCompany: 'Empresa activa',
      logout: 'Cerrar sesión',
      changePassword: 'Cambiar contraseña',
      currentPassword: 'Contraseña actual',
      newPassword: 'Nueva contraseña',
      confirmPassword: 'Confirmar contraseña',
      passwordChanged: 'Contraseña actualizada correctamente.',
      passwordChangeError: 'No se pudo actualizar la contraseña.',
      deleteAccount: 'Eliminar mi cuenta',
      deleteTitle: 'Eliminar cuenta definitivamente',
      deleteText: 'Se eliminará tu cuenta, tu solicitud de empresa, tu pertenencia a empresa, tus datos personales y los archivos asociados que pueda localizar. Esta acción no se puede deshacer.',
      deleteConfirm: 'Eliminar definitivamente',
      deleteError: 'No se pudo eliminar la cuenta.',
      nav: {
        notices: 'Avisos',
        agreement: 'Convenio',
        calendar: 'Calendario laboral',
        workers: 'Trabajadores',
        suggestions: 'Sugerencias',
        accessRequests: 'Peticiones de acceso',
        managers: 'Gestores'
      }
    },
    tutorial: {
      open: 'Tutorial',
      close: 'Cerrar tutorial',
      skip: 'Saltar',
      previous: 'Anterior',
      next: 'Siguiente',
      finish: 'Finalizar',
      steps: {
        brand: {
          title: 'Tu espacio de trabajo',
          text: 'Aquí ves el logo de Konvenio y la empresa que se está gestionando en este momento.'
        },
        company: {
          title: 'Empresa activa',
          text: 'Si gestionas varias empresas, usa este selector para cambiar los datos que muestra el panel.'
        },
        nav: {
          title: 'Apartados principales',
          text: 'Este menú abre cada área de gestión: avisos, convenio, calendario laboral y trabajadores.'
        },
        noticesNav: {
          title: 'Avisos',
          text: 'Usa los avisos para publicar comunicados internos que los trabajadores verán desde la app.'
        },
        noticesCreate: {
          title: 'Crear tablón',
          text: 'Este botón crea un nuevo tablón de avisos. Dentro de cada tablón puedes añadir avisos individuales.'
        },
        agreementNav: {
          title: 'Convenio',
          text: 'Este apartado guarda documentos laborales de la empresa, en PDF o como texto editable.'
        },
        agreementCreate: {
          title: 'Nuevo documento',
          text: 'Crea un documento, añade una descripción, elige texto o archivo y avisa a los trabajadores cuando sea necesario.'
        },
        calendarNav: {
          title: 'Calendario laboral',
          text: 'El calendario muestra festivos, días de empresa y la configuración anual de vacaciones o días personales.'
        },
        calendarAdd: {
          title: 'Acciones del calendario',
          text: 'Añade nuevos años laborales o abre un año existente para definir festivos y días no laborables.'
        },
        workersNav: {
          title: 'Trabajadores',
          text: 'Revisa solicitudes de acceso y gestiona quién puede ver el contenido de la empresa en la app.'
        },
        workersTabs: {
          title: 'Estado de trabajadores',
          text: 'Estas pestañas separan solicitudes pendientes, aceptadas y rechazadas para encontrar cada acción rápido.'
        },
        suggestionsNav: {
          title: 'Sugerencias',
          text: 'Consulta comentarios de trabajadores o envía ideas sobre el panel web desde este apartado.'
        },
        suggestionsRefresh: {
          title: 'Actualizar datos',
          text: 'Usa este botón para recargar las sugerencias y comprobar si hay comentarios nuevos.'
        },
        account: {
          title: 'Acciones de cuenta',
          text: 'En la parte inferior puedes cambiar contraseña, cerrar sesión o eliminar la cuenta actual.'
        },
        help: {
          title: 'Repetir el tutorial',
          text: 'Usa este botón cuando quieras volver a lanzar este recorrido guiado.'
        }
      }
    },
    landing: {
      heroTitle: 'Plataforma de gestión laboral para empresas',
      heroText: 'Konvenio facilita el acceso de los trabajadores a información laboral. Tú lo gestionas desde la web y ellos lo consultan desde el móvil.',
      // freeMonths: '3 primeros meses gratis',
      appTitle: 'La app es para los trabajadores de tu empresa',
      appText: 'Los trabajadores consultan avisos, convenios y calendario desde el móvil. Tú lo gestionas todo desde un panel web sencillo.',
      googlePlayAlt: 'Descargar Konvenio en Google Play',
      iosWebButton: 'Ver en iOS',
      iosWebAlt: 'Abrir la app web de Konvenio para iOS',
      bulletInvite: 'Alta de trabajadores por invitación',
      bulletRoles: 'Acceso por roles (gestor / trabajador)',
      bulletDocs: 'Notificaciones y documentación centralizada',
      pricingTitle: 'En fase piloto',
      pricingTextBefore: 'Empieza gratis durante',
      pricingTextStrong: '3 meses',
      pricingTextAfter: 'Después del piloto, revisaremos los siguientes pasos con tu empresa.',
      pilotIntro: 'Konvenio está actualmente en fase piloto con empresas seleccionadas.',
      pricingCaption: '* No se solicita tarjeta durante el piloto. Si más adelante se definen condiciones de pago, las confirmaremos contigo antes.',
      freePeriodLabel: 'Periodo gratuito:',
      freePeriodText: '3 meses de acceso completo para gestores y trabajadores',
      pilotMessageTitle: 'En fase piloto con las primeras empresas',
      pilotMessageText: 'Estamos abriendo el acceso de forma gradual para validar la plataforma con equipos reales. Durante esta etapa puedes probar Konvenio sin añadir tarjeta; el modelo comercial y cualquier condición futura se definirán más adelante.',
      notePanel: 'Panel web para gestores + app Android para trabajadores',
      noteTools: 'Avisos, convenios digitales y calendario laboral',
      noteRoles: 'Control por empresa y roles (cada trabajador ve solo lo suyo)',
      features: {
        noticesTitle: 'Avisos',
        noticesText: 'Notificaciones internas para trabajadores.',
        agreementsTitle: 'Convenios digitales',
        agreementsText: 'Acceso centralizado a documentación laboral.',
        calendarTitle: 'Calendario',
        calendarText: 'Calendario laboral, vacaciones y días festivos.',
        suggestionsTitle: 'Sugerencias',
        suggestionsText: 'Tenemos en cuenta tus sugerencias.'
      },
      faqTitle: 'Preguntas frecuentes',
      faqSubtitle: 'Lo esencial antes de empezar.',
      faqs: [
        {
          question: '¿Qué incluye el periodo gratuito?',
          answer: 'Incluye acceso completo durante 3 meses: panel web para gestores, app para trabajadores, avisos, convenios digitales y calendario laboral.'
        },
        {
          question: '¿Qué pasa después del piloto?',
          answer: 'Al finalizar el piloto, te contactaremos para revisar los siguientes pasos. Si las condiciones no te encajan, el acceso se desactiva sin ningún coste.'
        },
        {
          question: '¿Tiene coste para el trabajador?',
          answer: 'Los trabajadores usan la app sin coste. El modelo comercial para empresas se está definiendo durante el piloto.'
        },
        {
          question: '¿Necesito iPhone?',
          answer: 'De inicio la app está disponible en Android. La gestión se realiza desde web, por lo que el gestor no depende del móvil.'
        },
        {
          question: '¿Konvenio será de pago?',
          answer: 'El modelo todavía se está definiendo. Durante el piloto no hay tarjeta ni cobro automático, y si más adelante se establecen condiciones de pago, las confirmaremos contigo antes.'
        }
      ],
      footerTitle: 'Empieza sin riesgo',
      footerTextBefore: 'Pide acceso y prueba Konvenio',
      // footerTextStrong: '3 meses gratis',
      footerTextAfter: '.'
    },
    login: {
      welcome: 'Bienvenido a Konven.io',
      email: 'Correo electrónico',
      password: 'Contraseña',
      enter: 'Entrar',
      google: 'Entrar con Google',
      forgot: '¿Olvidaste la contraseña?',
      contact: 'Contacta:',
      missingCredentials: 'Introduce tu correo y contraseña.',
      missingResetEmail: 'Introduce tu correo para recuperar la contraseña.',
      passwordResetSent: 'Te hemos enviado un correo para restablecer la contraseña.',
      passwordResetError: 'No se pudo enviar el correo de recuperación.',
      googleRegistered: 'Usuario registrado con Google correctamente.',
      googleNoManagerAccess: 'Esta cuenta de Google no está registrada como gestor. Solicita registro o entra con una cuenta gestora ya aprobada.',
      noPermission: 'No tienes permisos para acceder al panel web.',
      accessUnavailable: 'Acceso no disponible',
      passwordsMismatch: 'Las contraseñas no coinciden.',
      registered: 'Usuario registrado correctamente.'
    },
    authErrors: {
      default: 'No se pudo completar la autenticación.',
      recentLoginRequired: 'Por seguridad, vuelve a iniciar sesión y elimina la cuenta justo después.',
      adminRestricted: 'Esta operación está restringida por Firebase.',
      popupAlreadyOpen: 'Ya hay una ventana de Google abierta. Termina ese intento o vuelve a probar.',
      emailInUse: 'Ya existe una cuenta con este correo.',
      invalidCredential: 'El correo o la contraseña no son correctos.',
      invalidEmail: 'El correo electrónico no tiene un formato válido.',
      missingEmail: 'Introduce tu correo electrónico.',
      missingPassword: 'Introduce tu contraseña.',
      networkFailed: 'No se pudo conectar con Firebase. Revisa tu conexión e inténtalo de nuevo.',
      operationNotAllowed: 'Este método de acceso no está habilitado en Firebase.',
      popupBlocked: 'El navegador ha bloqueado la ventana de Google. Permite ventanas emergentes para continuar.',
      popupClosed: 'Has cerrado la ventana de Google antes de completar el acceso.',
      tooManyRequests: 'Demasiados intentos fallidos. Espera unos minutos antes de volver a probar.',
      userDisabled: 'Esta cuenta está deshabilitada.',
      userNotFound: 'No existe ningún usuario con este correo.',
      weakPassword: 'La contraseña debe tener al menos 6 caracteres.',
      wrongPassword: 'La contraseña no es correcta.'
    },
    errors: {
      default: 'Algo no ha ido bien. Inténtalo de nuevo.',
      network: 'No se pudo conectar. Revisa tu conexión a internet e inténtalo de nuevo.',
      permissionDenied: 'No tienes permisos para realizar esta acción.',
      unauthenticated: 'Vuelve a iniciar sesión para continuar.',
      notFound: 'No se encontró la información solicitada.',
      fileNotFound: 'No se encontró el archivo.',
      timeout: 'La operación ha tardado demasiado. Inténtalo de nuevo.',
      serviceUnavailable: 'El servicio no está disponible temporalmente. Inténtalo de nuevo en unos minutos.',
      resourceExhausted: 'El servicio está temporalmente saturado. Inténtalo más tarde.',
      failedPrecondition: 'La operación no se puede completar con los datos actuales.',
      cancelled: 'La operación se ha cancelado.',
      adminRestricted: 'Esta operación está restringida por Firebase.',
      popupAlreadyOpen: 'Ya hay una ventana de Google abierta. Termina ese intento o vuelve a probar.',
      emailInUse: 'Ya existe una cuenta con este correo.',
      invalidCredential: 'El correo o la contraseña no son correctos.',
      invalidEmail: 'El correo electrónico no tiene un formato válido.',
      missingEmail: 'Introduce tu correo electrónico.',
      missingPassword: 'Introduce tu contraseña.',
      operationNotAllowed: 'Este método de acceso no está habilitado.',
      popupBlocked: 'El navegador ha bloqueado la ventana de Google. Permite ventanas emergentes para continuar.',
      popupClosed: 'Has cerrado la ventana de Google antes de completar el acceso.',
      recentLoginRequired: 'Por seguridad, vuelve a iniciar sesión e inténtalo justo después.',
      tooManyRequests: 'Demasiados intentos fallidos. Espera unos minutos antes de volver a probar.',
      userDisabled: 'Esta cuenta está deshabilitada.',
      userNotFound: 'No existe ningún usuario con este correo.',
      weakPassword: 'La contraseña debe tener al menos 6 caracteres.',
      wrongPassword: 'La contraseña no es correcta.'
    },
    requestAccess: {
      title: 'Solicitar acceso',
      subtitle: 'Acceso para gestores y comités de empresa. Los trabajadores se registran desde la app.',
      reviewBefore: 'Revisamos las solicitudes en un plazo aproximado de',
      reviewStrong: '24-48 horas laborables',
      reviewAfter: 'Recibirás la respuesta en el correo electrónico indicado.',
      name: 'Nombre y apellidos',
      email: 'Email',
      company: 'Empresa',
      address: 'Dirección de la empresa',
      phone: 'Teléfono',
      accessMethod: '¿Cómo deseas el acceso?',
      accessMethodGoogle: 'Mediante Google',
      accessMethodPassword: 'Mediante usuario y contraseña',
      accessMethodShortGoogle: 'Google',
      accessMethodShortPassword: 'Usuario y contraseña',
      notes: 'Comentarios adicionales',
      privacyLink: 'Política de privacidad',
      legalNoticeLink: 'Aviso legal',
      cookiesLink: 'Política de cookies',
      legalAcceptPrefix: 'Acepto las condiciones legales:',
      legalAcceptAnd: 'y la',
      submit: 'Enviar solicitud',
      successBefore: 'Solicitud enviada correctamente.',
      successAfter: 'Te responderemos por correo en cuanto sea revisada.',
      question: '¿Tienes alguna duda?',
      writeUs: 'Escríbenos a',
      required: 'Campo obligatorio',
      invalidEmail: 'Email no válido',
      acceptedRequired: 'Debes aceptar el tratamiento de datos',
      submitError: 'Error al enviar la solicitud. Inténtalo de nuevo.'
    },
    cookies: {
      message: 'Utilizamos cookies analíticas para mejorar la experiencia. Puedes aceptar o rechazar su uso.',
      analyticsEnabled: 'Cookies aceptadas: Analytics habilitado'
    },
    statuses: {
      pending: 'Pendiente',
      reviewed: 'Revisada',
      accepted: 'Aceptada',
      rejected: 'Rechazada'
    },
    admin: {
      accessRequests: {
        title: 'Peticiones de acceso',
        empty: 'No hay peticiones de acceso.',
        documentTitle: 'Ver documento',
        updated: 'Estado actualizado.',
        loadError: 'No se pudieron cargar las peticiones.',
        updateError: 'No se pudo actualizar el estado.'
      },
      managers: {
        title: 'Gestión de gestores',
        companyFormTitle: 'Alta de empresa',
        managerFormTitle: 'Alta de gestor',
        companyName: 'Nombre de empresa',
        contactEmail: 'Email de contacto',
        temporaryPassword: 'Contraseña temporal',
        password: 'Contraseña',
        googlePending: 'Google pendiente',
        passwordGoogle: 'password/google',
        companyManagers: 'Gestores de la empresa',
        backToCompanies: 'Volver a empresas',
        emptyManagers: 'No hay gestores en esta empresa.',
        createdCompany: 'Empresa creada correctamente.',
        createdManager: 'Gestor creado correctamente.',
        createdManagerWithVerification: 'Gestor creado correctamente. Se ha enviado un correo de verificación para confirmar la dirección de email.',
        promotedExistingManager: 'Usuario existente promocionado a gestor. Mantendrá su método de acceso actual.',
        deletedCompany: 'Empresa eliminada correctamente.',
        deletedManager: 'Gestor eliminado correctamente.',
        companyCreateError: 'No se pudo crear la empresa.',
        companyDeleteError: 'No se pudo eliminar la empresa.',
        managerCreateError: 'No se pudo crear el gestor.',
        managerDeleteError: 'No se pudo eliminar el gestor.',
        deleteCompanyConfirm: '¿Eliminar la empresa "{name}"?',
        deleteCompanyWarning: 'Se eliminarán la empresa, sus avisos, calendarios laborales, asociaciones con trabajadores y accesos de gestores de esta empresa. Esta acción no se puede deshacer.',
        deleteConfirm: '¿Eliminar al gestor "{name}"?',
        deleteLastManagerWarning: 'Este es el único gestor de esta empresa. Si lo eliminas, la empresa se quedará sin gestores con acceso web.',
        selectValidCompany: 'Selecciona una empresa válida.',
        loadError: 'No se pudieron cargar los datos.'
      }
    },
    workers: {
      title: 'Gestión de trabajadores',
      pending: 'Pendientes',
      accepted: 'Aceptados',
      rejected: 'Rechazados',
      searchPlaceholder: 'Buscar por nombre o email',
      emptySearch: 'No hay trabajadores que coincidan con la búsqueda',
      emptyPending: 'No hay solicitudes pendientes',
      emptyAccepted: 'No hay trabajadores',
      emptyRejected: 'No hay solicitudes rechazadas',
      acceptedNotificationTitle: 'Solicitud aceptada',
      acceptedNotificationBody: 'Ya tienes acceso a {company}.',
      rejectedNotificationTitle: 'Solicitud rechazada',
      rejectedNotificationBody: 'Tu solicitud para {company} ha sido rechazada.',
      yourCompany: 'tu empresa',
      theCompany: 'la empresa',
      loadError: 'No se pudieron cargar los trabajadores.',
      updateError: 'No se pudo actualizar la solicitud del trabajador.',
      acceptError: 'No se pudo aceptar la solicitud.',
      rejectError: 'No se pudo rechazar la solicitud.',
      revokeError: 'No se pudo revocar el acceso del trabajador.',
      restoreError: 'No se pudo restaurar la solicitud.',
      acceptedNotificationError: 'No se pudo enviar la notificación de aceptación.',
      rejectedNotificationError: 'No se pudo enviar la notificación de rechazo.'
    },
    notices: {
      title: 'Avisos',
      newNotice: 'Nuevo aviso',
      editNotice: 'Editar aviso',
      noticeTitle: 'Título del aviso',
      description: 'Descripción',
      saveNotice: 'Guardar aviso',
      seeMore: 'Ver más',
      emptyBoard: 'Este tablón todavía no tiene avisos.',
      newBoard: 'Nuevo tablón',
      editBoard: 'Editar tablón',
      boardTitle: 'Título del tablón',
      deleteBoardConfirm: '¿Eliminar este tablón y todos sus avisos?',
      deleteNoticeConfirm: '¿Eliminar este aviso?',
      notificationTitle: 'Nuevo aviso',
      notificationError: 'No se pudo enviar la notificación del aviso:',
      loadError: 'No se pudieron cargar los avisos.',
      saveBoardError: 'No se pudo guardar el tablón.',
      deleteBoardError: 'No se pudo eliminar el tablón.',
      saveNoticeError: 'No se pudo guardar el aviso.',
      deleteNoticeError: 'No se pudo eliminar el aviso.'
    },
    agreement: {
      title: 'Convenio',
      newDocument: 'Nuevo documento',
      editDocument: 'Editar documento',
      documentTitle: 'Título',
      description: 'Descripción',
      maxDescription: 'Máximo 500 caracteres',
      documentType: 'Tipo de documento',
      editableText: 'Texto editable',
      pdfDocument: 'Documento PDF',
      currentDocument: 'Documento actual:',
      replaceFile: 'Reemplazar archivo (opcional)',
      uploadFile: 'Subir archivo',
      saveChanges: 'Guardar cambios',
      createDocument: 'Crear documento',
      viewDocument: 'Ver documento',
      viewText: 'Ver texto',
      deleteConfirm: '¿Eliminar "{title}"?',
      notificationTitle: 'Nuevo documento del convenio',
      notificationError: 'No se pudo enviar la notificación del convenio:',
      loadError: 'No se pudieron cargar los documentos del convenio.',
      saveError: 'No se pudo guardar el documento del convenio.',
      deleteError: 'No se pudo eliminar el documento del convenio.'
    },
    calendar: {
      title: 'Calendarios laborales',
      addYear: 'Añadir nuevo año laboral',
      workYear: 'Año laboral {year}',
      addHoliday: 'Añadir día no laborable',
      editHoliday: 'Editar día no laborable',
      vacation: 'Vacaciones',
      personalDays: 'Días personales',
      national: 'Nacional',
      regional: 'Autonómico',
      company: 'Empresa',
      holidaysTitle: 'Días no laborables {year}',
      reason: 'Motivo',
      addRow: 'Añadir fila',
      completeRows: 'Completa todas las filas antes de guardar.',
      emptyHolidays: 'No hay días no laborables definidos.',
      deleteTitle: 'Eliminar festivo',
      deleteConfirm: '¿Seguro que deseas eliminar este día no laborable?',
      loadError: 'No se pudieron cargar los calendarios laborales.',
      saveError: 'No se pudo guardar el calendario laboral.',
      deleteError: 'No se pudo eliminar el día no laborable.'
    },
    suggestions: {
      title: 'Sugerencias',
      appTab: 'Sugerencias de la app',
      platformTab: 'Sugerencias de la plataforma',
      companiesTab: 'Sugerencias de empresas',
      agreementTitle: 'Sugerencias del convenio',
      platformTitle: 'Sugerencia sobre la plataforma',
      platformIntro: 'Envía ideas, mejoras o cualquier aportación sobre el panel web de Konvenio.',
      placeholder: 'Escribe tu sugerencia aquí',
      send: 'Enviar sugerencia',
      emptyCategory: 'No hay sugerencias en esta categoría.',
      emptyAgreement: 'No hay sugerencias del convenio para tu empresa.',
      loadError: 'No se pudieron cargar las sugerencias.',
      missingText: 'Por favor escribe una sugerencia antes de enviarla.',
      missingCompany: 'No se encuentra el nombre de la empresa.',
      thanks: 'Gracias por tu aportación.',
      saveError: 'Hubo un problema guardando la sugerencia.',
      viewed: 'Visualizada',
      notViewed: 'Sin visualizar',
      markViewed: 'Marcar como visualizada',
      markViewedSuccess: 'Sugerencia marcada como visualizada.',
      markViewedError: 'No se pudo marcar la sugerencia como visualizada.',
      types: {
        agreement: 'Convenio',
        app: 'App',
        platform: 'Plataforma'
      }
    },
    info: {
      legalTitle: 'Aviso Legal',
      privacyTitle: 'Política de Privacidad',
      cookiesTitle: 'Política de Cookies',
      intro: 'Esta página contiene la información legal de Konvenio.',
      owner: 'Titular del sitio web',
      purpose: 'Finalidad',
      dataProtection: 'Protección de datos',
      cookies: 'Cookies',
      contact: 'Contacto',
      country: 'País',
      spain: 'España',
      rights: 'Derechos',
      rightsText: 'El usuario puede ejercer sus derechos de acceso, rectificación, supresión, limitación, oposición y portabilidad contactando con Konvenio.',
      dataCategories: 'Categorías de datos',
      processingPurposes: 'Finalidades del tratamiento',
      retention: 'Conservación de los datos',
      providers: 'Servicios de terceros',
      moreInfo: 'Más información'
    }
  },
  pt: {
    common: {
      appName: 'Konvenio',
      back: 'Voltar atrás',
      startFree: 'Solicitar registo',
      or: 'ou',
      managerAccess: 'Iniciar sessão',
      viewPricing: 'Fase piloto',
      backHome: 'Voltar ao início',
      accept: 'Aceitar',
      reject: 'Rejeitar',
      moreInfo: 'Mais informação',
      refresh: 'Atualizar',
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      edit: 'Editar',
      close: 'Fechar',
      create: 'Criar',
      actions: 'Ações',
      date: 'Data',
      type: 'Tipo',
      status: 'Estado',
      company: 'Empresa',
      companies: 'Empresas',
      user: 'Utilizador',
      email: 'Email',
      phone: 'Telefone',
      address: 'Endereço',
      name: 'Nome',
      lastName: 'Apelidos',
      document: 'Documento',
      notes: 'Notas',
      contact: 'Contacto',
      author: 'Autor',
      access: 'Acesso',
      createdAt: 'Registo',
      required: 'Campo obrigatório',
      invalidEmail: 'Email inválido',
      minPassword: 'Mínimo 6 caracteres',
      noName: 'Sem nome',
      noCompany: 'Sem empresa',
      view: 'Ver',
      current: 'Atual',
      confirmAction: 'Confirmar ação',
      unknownUser: 'Utilizador',
      unknownCompany: 'Empresa',
      loadError: 'Não foi possível carregar os dados.',
      updateError: 'Não foi possível atualizar o estado.'
    },
    appShell: {
      activeCompany: 'Empresa ativa',
      logout: 'Terminar sessão',
      changePassword: 'Alterar palavra-passe',
      currentPassword: 'Palavra-passe atual',
      newPassword: 'Nova palavra-passe',
      confirmPassword: 'Confirmar palavra-passe',
      passwordChanged: 'Palavra-passe atualizada corretamente.',
      passwordChangeError: 'Não foi possível atualizar a palavra-passe.',
      deleteAccount: 'Eliminar a minha conta',
      deleteTitle: 'Eliminar conta definitivamente',
      deleteText: 'A sua conta, pedido de empresa, vínculo à empresa, dados pessoais e ficheiros associados que possam ser localizados serão eliminados. Esta ação não pode ser anulada.',
      deleteConfirm: 'Eliminar definitivamente',
      deleteError: 'Não foi possível eliminar a conta.',
      nav: {
        notices: 'Avisos',
        agreement: 'Acordo',
        calendar: 'Calendário laboral',
        workers: 'Trabalhadores',
        suggestions: 'Sugestões',
        accessRequests: 'Pedidos de acesso',
        managers: 'Gestores'
      }
    },
    tutorial: {
      open: 'Tutorial',
      close: 'Fechar tutorial',
      skip: 'Saltar',
      previous: 'Anterior',
      next: 'Seguinte',
      finish: 'Finalizar',
      steps: {
        brand: {
          title: 'O seu espaço de trabalho',
          text: 'Aqui vê o logótipo da Konvenio e a empresa que está a ser gerida neste momento.'
        },
        company: {
          title: 'Empresa ativa',
          text: 'Se gerir várias empresas, use este seletor para alterar os dados apresentados no painel.'
        },
        nav: {
          title: 'Secções principais',
          text: 'Este menu abre cada área de gestão: avisos, acordo, calendário laboral e trabalhadores.'
        },
        noticesNav: {
          title: 'Avisos',
          text: 'Use os avisos para publicar comunicados internos que os trabalhadores veem na app.'
        },
        noticesCreate: {
          title: 'Criar quadro',
          text: 'Este botão cria um novo quadro de avisos. Dentro de cada quadro pode adicionar avisos individuais.'
        },
        agreementNav: {
          title: 'Acordo',
          text: 'Esta secção guarda documentos laborais da empresa, em PDF ou como texto editável.'
        },
        agreementCreate: {
          title: 'Novo documento',
          text: 'Crie um documento, adicione uma descrição, escolha texto ou ficheiro e avise os trabalhadores quando necessário.'
        },
        calendarNav: {
          title: 'Calendário laboral',
          text: 'O calendário mostra feriados, dias da empresa e a configuração anual de férias ou dias pessoais.'
        },
        calendarAdd: {
          title: 'Ações do calendário',
          text: 'Adicione novos anos laborais ou abra um ano existente para definir feriados e dias não laboráveis.'
        },
        workersNav: {
          title: 'Trabalhadores',
          text: 'Reveja pedidos de acesso e gira quem pode ver o conteúdo da empresa na app.'
        },
        workersTabs: {
          title: 'Estado dos trabalhadores',
          text: 'Estas abas separam pedidos pendentes, aceites e rejeitados para encontrar cada ação rapidamente.'
        },
        suggestionsNav: {
          title: 'Sugestões',
          text: 'Consulte comentários dos trabalhadores ou envie ideias sobre o painel web a partir desta secção.'
        },
        suggestionsRefresh: {
          title: 'Atualizar dados',
          text: 'Use este botão para recarregar as sugestões e verificar se há novos comentários.'
        },
        account: {
          title: 'Ações da conta',
          text: 'Na parte inferior pode alterar a palavra-passe, terminar sessão ou eliminar a conta atual.'
        },
        help: {
          title: 'Repetir o tutorial',
          text: 'Use este botão sempre que quiser iniciar novamente este percurso guiado.'
        }
      }
    },
    landing: {
      heroTitle: 'Plataforma de gestão laboral para empresas',
      heroText: 'A Konvenio facilita o acesso dos trabalhadores a informação laboral. Você gere tudo pela web e eles consultam pelo telemóvel.',
      // freeMonths: 'Primeiros 3 meses grátis',
      appTitle: 'A app é para os trabalhadores da sua empresa',
      appText: 'Os colaboradores consultam avisos, acordos e calendário pelo telemóvel. Você gere tudo a partir de um painel web simples.',
      googlePlayAlt: 'Transferir Konvenio no Google Play',
      iosWebButton: 'Ver no iOS',
      iosWebAlt: 'Abrir a app web da Konvenio para iOS',
      bulletInvite: 'Registo de colaboradores por convite',
      bulletRoles: 'Acesso por funções (gestor / colaborador)',
      bulletDocs: 'Notificações e documentação centralizada',
      pricingTitle: 'Em fase piloto',
      pricingTextBefore: 'Comece grátis durante',
      pricingTextStrong: '3 meses',
      pricingTextAfter: 'Depois do piloto, iremos rever os próximos passos com a sua empresa.',
      pilotIntro: 'A Konvenio está atualmente em fase piloto com empresas selecionadas.',
      pricingCaption: '* Não é solicitado cartão durante o piloto. Se mais tarde forem definidas condições de pagamento, iremos confirmá-las consigo primeiro.',
      freePeriodLabel: 'Período gratuito:',
      freePeriodText: '3 meses de acesso completo para gestores e colaboradores',
      pilotMessageTitle: 'Em fase piloto com as primeiras empresas',
      pilotMessageText: 'Estamos a abrir o acesso gradualmente para validar a plataforma com equipas reais. Durante esta fase pode experimentar a Konvenio sem adicionar cartão; o modelo comercial e quaisquer condições futuras serão definidos mais tarde.',
      notePanel: 'Painel web para gestores + app Android para colaboradores',
      noteTools: 'Avisos, acordos digitais e calendário laboral',
      noteRoles: 'Controlo por empresa e funções (cada colaborador vê apenas o seu conteúdo)',
      features: {
        noticesTitle: 'Avisos',
        noticesText: 'Notificações internas para colaboradores.',
        agreementsTitle: 'Acordos digitais',
        agreementsText: 'Acesso centralizado à documentação laboral.',
        calendarTitle: 'Calendário',
        calendarText: 'Calendário laboral, férias e feriados.',
        suggestionsTitle: 'Sugestões',
        suggestionsText: 'Temos em conta as suas sugestões.'
      },
      faqTitle: 'Perguntas frequentes',
      faqSubtitle: 'O essencial antes de começar.',
      faqs: [
        {
          question: 'O que inclui o período gratuito?',
          answer: 'Inclui acesso completo durante 3 meses: painel web para gestores, app para colaboradores, avisos, acordos digitais e calendário laboral.'
        },
        {
          question: 'O que acontece depois do piloto?',
          answer: 'No final do piloto, entraremos em contacto para rever os próximos passos. Se as condições não forem adequadas, o acesso é desativado sem qualquer custo.'
        },
        {
          question: 'Tem custo para o colaborador?',
          answer: 'Os colaboradores usam a app sem custo. O modelo comercial para empresas está a ser definido durante o piloto.'
        },
        {
          question: 'Preciso de iPhone?',
          answer: 'Inicialmente a app está disponível em Android. A gestão é feita pela web, por isso o gestor não depende do telemóvel.'
        },
        {
          question: 'A Konvenio será paga?',
          answer: 'O modelo ainda está a ser definido. Durante o piloto não há cartão nem cobrança automática, e se mais tarde forem estabelecidas condições de pagamento, iremos confirmá-las consigo primeiro.'
        }
      ],
      footerTitle: 'Comece sem risco',
      footerTextBefore: 'Peça acesso e experimente a Konvenio',
      // footerTextStrong: '3 meses grátis',
      footerTextAfter: '.'
    },
    login: {
      welcome: 'Bem-vindo à Konven.io',
      email: 'Email',
      password: 'Palavra-passe',
      enter: 'Entrar',
      google: 'Entrar com Google',
      forgot: 'Esqueceu-se da palavra-passe?',
      contact: 'Contacte:',
      missingCredentials: 'Introduza o seu email e palavra-passe.',
      missingResetEmail: 'Introduza o seu email para recuperar a palavra-passe.',
      passwordResetSent: 'Enviámos-lhe um email para redefinir a palavra-passe.',
      passwordResetError: 'Não foi possível enviar o email de recuperação.',
      googleRegistered: 'Utilizador registado com Google corretamente.',
      googleNoManagerAccess: 'Esta conta Google não está registada como gestor. Solicite registo ou entre com uma conta de gestor já aprovada.',
      noPermission: 'Não tem permissões para aceder ao painel web.',
      accessUnavailable: 'Acesso indisponível',
      passwordsMismatch: 'As palavras-passe não coincidem.',
      registered: 'Utilizador registado corretamente.'
    },
    authErrors: {
      default: 'Não foi possível concluir a autenticação.',
      recentLoginRequired: 'Por segurança, inicie sessão novamente e elimine a conta logo depois.',
      adminRestricted: 'Esta operação está restringida pelo Firebase.',
      popupAlreadyOpen: 'Já existe uma janela do Google aberta. Termine essa tentativa ou tente novamente.',
      emailInUse: 'Já existe uma conta com este email.',
      invalidCredential: 'O email ou a palavra-passe não estão corretos.',
      invalidEmail: 'O email não tem um formato válido.',
      missingEmail: 'Introduza o seu email.',
      missingPassword: 'Introduza a sua palavra-passe.',
      networkFailed: 'Não foi possível ligar ao Firebase. Verifique a ligação e tente novamente.',
      operationNotAllowed: 'Este método de acesso não está ativado no Firebase.',
      popupBlocked: 'O navegador bloqueou a janela do Google. Permita pop-ups para continuar.',
      popupClosed: 'Fechou a janela do Google antes de concluir o acesso.',
      tooManyRequests: 'Demasiadas tentativas falhadas. Aguarde alguns minutos antes de tentar novamente.',
      userDisabled: 'Esta conta está desativada.',
      userNotFound: 'Não existe nenhum utilizador com este email.',
      weakPassword: 'A palavra-passe deve ter pelo menos 6 caracteres.',
      wrongPassword: 'A palavra-passe não está correta.'
    },
    errors: {
      default: 'Algo correu mal. Tente novamente.',
      network: 'Não foi possível ligar. Verifique a ligação à internet e tente novamente.',
      permissionDenied: 'Não tem permissões para realizar esta ação.',
      unauthenticated: 'Inicie sessão novamente para continuar.',
      notFound: 'Não foi possível encontrar a informação solicitada.',
      fileNotFound: 'Não foi possível encontrar o ficheiro.',
      timeout: 'A operação demorou demasiado. Tente novamente.',
      serviceUnavailable: 'O serviço está temporariamente indisponível. Tente novamente dentro de alguns minutos.',
      resourceExhausted: 'O serviço está temporariamente sobrecarregado. Tente mais tarde.',
      failedPrecondition: 'A operação não pode ser concluída com os dados atuais.',
      cancelled: 'A operação foi cancelada.',
      adminRestricted: 'Esta operação está restringida pelo Firebase.',
      popupAlreadyOpen: 'Já existe uma janela do Google aberta. Termine essa tentativa ou tente novamente.',
      emailInUse: 'Já existe uma conta com este email.',
      invalidCredential: 'O email ou a palavra-passe não estão corretos.',
      invalidEmail: 'O email não tem um formato válido.',
      missingEmail: 'Introduza o seu email.',
      missingPassword: 'Introduza a sua palavra-passe.',
      operationNotAllowed: 'Este método de acesso não está ativado.',
      popupBlocked: 'O navegador bloqueou a janela do Google. Permita pop-ups para continuar.',
      popupClosed: 'Fechou a janela do Google antes de concluir o acesso.',
      recentLoginRequired: 'Por segurança, inicie sessão novamente e tente a ação logo depois.',
      tooManyRequests: 'Demasiadas tentativas falhadas. Aguarde alguns minutos antes de tentar novamente.',
      userDisabled: 'Esta conta está desativada.',
      userNotFound: 'Não existe nenhum utilizador com este email.',
      weakPassword: 'A palavra-passe deve ter pelo menos 6 caracteres.',
      wrongPassword: 'A palavra-passe não está correta.'
    },
    requestAccess: {
      title: 'Solicitar acesso',
      subtitle: 'Acesso para gestores e comissões de trabalhadores. Os trabalhadores registam-se a partir da app.',
      reviewBefore: 'Revemos as solicitações num prazo aproximado de',
      reviewStrong: '24-48 horas úteis',
      reviewAfter: 'Receberá a resposta no email indicado.',
      name: 'Nome completo',
      email: 'Email',
      company: 'Empresa',
      address: 'Endereço da empresa',
      phone: 'Telefone',
      accessMethod: 'Como pretende aceder?',
      accessMethodGoogle: 'Através do Google',
      accessMethodPassword: 'Através de utilizador e palavra-passe',
      accessMethodShortGoogle: 'Google',
      accessMethodShortPassword: 'Utilizador e palavra-passe',
      notes: 'Comentários adicionais',
      privacyLink: 'Política de Privacidade',
      legalNoticeLink: 'Aviso Legal',
      cookiesLink: 'Política de Cookies',
      legalAcceptPrefix: 'Aceito as condições legais:',
      legalAcceptAnd: 'e a',
      submit: 'Enviar solicitação',
      successBefore: 'Solicitação enviada corretamente.',
      successAfter: 'Responderemos por email assim que for revista.',
      question: 'Tem alguma dúvida?',
      writeUs: 'Escreva-nos para',
      required: 'Campo obrigatório',
      invalidEmail: 'Email inválido',
      acceptedRequired: 'Deve aceitar o tratamento de dados',
      submitError: 'Erro ao enviar a solicitação. Tente novamente.'
    },
    cookies: {
      message: 'Utilizamos cookies analíticos para melhorar a experiência. Pode aceitar ou rejeitar a sua utilização.',
      analyticsEnabled: 'Cookies aceites: Analytics ativado'
    },
    statuses: {
      pending: 'Pendente',
      reviewed: 'Revisto',
      accepted: 'Aceite',
      rejected: 'Rejeitado'
    },
    admin: {
      accessRequests: {
        title: 'Pedidos de acesso',
        empty: 'Não há pedidos de acesso.',
        documentTitle: 'Ver documento',
        updated: 'Estado atualizado.',
        loadError: 'Não foi possível carregar os pedidos.',
        updateError: 'Não foi possível atualizar o estado.'
      },
      managers: {
        title: 'Gestão de gestores',
        companyFormTitle: 'Criar empresa',
        managerFormTitle: 'Criar gestor',
        companyName: 'Nome da empresa',
        contactEmail: 'Email de contacto',
        temporaryPassword: 'Palavra-passe temporária',
        password: 'Palavra-passe',
        googlePending: 'Google pendente',
        passwordGoogle: 'palavra-passe/google',
        companyManagers: 'Gestores da empresa',
        backToCompanies: 'Voltar a empresas',
        emptyManagers: 'Não há gestores nesta empresa.',
        createdCompany: 'Empresa criada corretamente.',
        createdManager: 'Gestor criado corretamente.',
        createdManagerWithVerification: 'Gestor criado corretamente. Foi enviado um email de verificação para confirmar o endereço de email.',
        promotedExistingManager: 'Utilizador existente promovido a gestor. Irá manter o método de acesso atual.',
        deletedCompany: 'Empresa eliminada corretamente.',
        deletedManager: 'Gestor eliminado corretamente.',
        companyCreateError: 'Não foi possível criar a empresa.',
        companyDeleteError: 'Não foi possível eliminar a empresa.',
        managerCreateError: 'Não foi possível criar o gestor.',
        managerDeleteError: 'Não foi possível eliminar o gestor.',
        deleteCompanyConfirm: 'Eliminar a empresa "{name}"?',
        deleteCompanyWarning: 'Serão eliminados a empresa, os seus avisos, calendários laborais, associações com trabalhadores e acessos de gestores desta empresa. Esta ação não pode ser anulada.',
        deleteConfirm: 'Eliminar o gestor "{name}"?',
        deleteLastManagerWarning: 'Este é o único gestor desta empresa. Se o eliminar, a empresa ficará sem gestores com acesso web.',
        selectValidCompany: 'Selecione uma empresa válida.',
        loadError: 'Não foi possível carregar os dados.'
      }
    },
    workers: {
      title: 'Gestão de trabalhadores',
      pending: 'Pendentes',
      accepted: 'Aceites',
      rejected: 'Rejeitados',
      searchPlaceholder: 'Pesquisar por nome ou email',
      emptySearch: 'Não há trabalhadores que correspondam à pesquisa',
      emptyPending: 'Não há pedidos pendentes',
      emptyAccepted: 'Não há trabalhadores',
      emptyRejected: 'Não há pedidos rejeitados',
      acceptedNotificationTitle: 'Pedido aceite',
      acceptedNotificationBody: 'Já tem acesso a {company}.',
      rejectedNotificationTitle: 'Pedido rejeitado',
      rejectedNotificationBody: 'O seu pedido para {company} foi rejeitado.',
      yourCompany: 'a sua empresa',
      theCompany: 'a empresa',
      loadError: 'Não foi possível carregar os trabalhadores.',
      updateError: 'Não foi possível atualizar o pedido do trabalhador.',
      acceptError: 'Não foi possível aceitar o pedido.',
      rejectError: 'Não foi possível rejeitar o pedido.',
      revokeError: 'Não foi possível revogar o acesso do trabalhador.',
      restoreError: 'Não foi possível restaurar o pedido.',
      acceptedNotificationError: 'Não foi possível enviar a notificação de aceitação.',
      rejectedNotificationError: 'Não foi possível enviar a notificação de rejeição.'
    },
    notices: {
      title: 'Avisos',
      newNotice: 'Novo aviso',
      editNotice: 'Editar aviso',
      noticeTitle: 'Título do aviso',
      description: 'Descrição',
      saveNotice: 'Guardar aviso',
      seeMore: 'Ver mais',
      emptyBoard: 'Este quadro ainda não tem avisos.',
      newBoard: 'Novo quadro',
      editBoard: 'Editar quadro',
      boardTitle: 'Título do quadro',
      deleteBoardConfirm: 'Eliminar este quadro e todos os seus avisos?',
      deleteNoticeConfirm: 'Eliminar este aviso?',
      notificationTitle: 'Novo aviso',
      notificationError: 'Não foi possível enviar a notificação do aviso:',
      loadError: 'Não foi possível carregar os avisos.',
      saveBoardError: 'Não foi possível guardar o quadro.',
      deleteBoardError: 'Não foi possível eliminar o quadro.',
      saveNoticeError: 'Não foi possível guardar o aviso.',
      deleteNoticeError: 'Não foi possível eliminar o aviso.'
    },
    agreement: {
      title: 'Acordo',
      newDocument: 'Novo documento',
      editDocument: 'Editar documento',
      documentTitle: 'Título',
      description: 'Descrição',
      maxDescription: 'Máximo 500 caracteres',
      documentType: 'Tipo de documento',
      editableText: 'Texto editável',
      pdfDocument: 'Documento PDF',
      currentDocument: 'Documento atual:',
      replaceFile: 'Substituir ficheiro (opcional)',
      uploadFile: 'Carregar ficheiro',
      saveChanges: 'Guardar alterações',
      createDocument: 'Criar documento',
      viewDocument: 'Ver documento',
      viewText: 'Ver texto',
      deleteConfirm: 'Eliminar "{title}"?',
      notificationTitle: 'Novo documento do acordo',
      notificationError: 'Não foi possível enviar a notificação do acordo:',
      loadError: 'Não foi possível carregar os documentos do acordo.',
      saveError: 'Não foi possível guardar o documento do acordo.',
      deleteError: 'Não foi possível eliminar o documento do acordo.'
    },
    calendar: {
      title: 'Calendários laborais',
      addYear: 'Adicionar novo ano laboral',
      workYear: 'Ano laboral {year}',
      addHoliday: 'Adicionar dia não laboral',
      editHoliday: 'Editar dia não laboral',
      vacation: 'Férias',
      personalDays: 'Dias pessoais',
      national: 'Nacional',
      regional: 'Regional',
      company: 'Empresa',
      holidaysTitle: 'Dias não laborais {year}',
      reason: 'Motivo',
      addRow: 'Adicionar linha',
      completeRows: 'Preencha todas as linhas antes de guardar.',
      emptyHolidays: 'Não há dias não laborais definidos.',
      deleteTitle: 'Eliminar feriado',
      deleteConfirm: 'Tem a certeza de que deseja eliminar este dia não laboral?',
      loadError: 'Não foi possível carregar os calendários laborais.',
      saveError: 'Não foi possível guardar o calendário laboral.',
      deleteError: 'Não foi possível eliminar o dia não laboral.'
    },
    suggestions: {
      title: 'Sugestões',
      appTab: 'Sugestões da app',
      platformTab: 'Sugestões da plataforma',
      companiesTab: 'Sugestões de empresas',
      agreementTitle: 'Sugestões do acordo',
      platformTitle: 'Sugestão sobre a plataforma',
      platformIntro: 'Envie ideias, melhorias ou qualquer contribuição sobre o painel web da Konvenio.',
      placeholder: 'Escreva a sua sugestão aqui',
      send: 'Enviar sugestão',
      emptyCategory: 'Não há sugestões nesta categoria.',
      emptyAgreement: 'Não há sugestões do acordo para a sua empresa.',
      loadError: 'Não foi possível carregar as sugestões.',
      missingText: 'Escreva uma sugestão antes de enviar.',
      missingCompany: 'Não foi possível encontrar o nome da empresa.',
      thanks: 'Obrigado pela sua contribuição.',
      saveError: 'Houve um problema ao guardar a sugestão.',
      viewed: 'Visualizada',
      notViewed: 'Por visualizar',
      markViewed: 'Marcar como visualizada',
      markViewedSuccess: 'Sugestão marcada como visualizada.',
      markViewedError: 'Não foi possível marcar a sugestão como visualizada.',
      types: {
        agreement: 'Acordo',
        app: 'App',
        platform: 'Plataforma'
      }
    },
    info: {
      legalTitle: 'Aviso Legal',
      privacyTitle: 'Política de Privacidade',
      cookiesTitle: 'Política de Cookies',
      intro: 'Esta página contém a informação legal da Konvenio.',
      owner: 'Titular do site',
      purpose: 'Finalidade',
      dataProtection: 'Proteção de dados',
      cookies: 'Cookies',
      contact: 'Contacto',
      country: 'País',
      spain: 'Espanha',
      rights: 'Direitos',
      rightsText: 'O utilizador pode exercer os seus direitos de acesso, retificação, eliminação, limitação, oposição e portabilidade contactando a Konvenio.',
      dataCategories: 'Categorias de dados',
      processingPurposes: 'Finalidades do tratamento',
      retention: 'Conservação dos dados',
      providers: 'Serviços de terceiros',
      moreInfo: 'Mais informação'
    }
  }
}

function normalizeLocale(locale) {
  if (!locale) return null
  const language = locale.toLowerCase().split('-')[0]
  return supportedLocales.includes(language) ? language : null
}

function detectLocale() {
  const browserLocales = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language]

  return browserLocales.map(normalizeLocale).find(Boolean) || 'en'
}

const savedLocale = normalizeLocale(localStorage.getItem(localeStorageKey))
const locale = savedLocale || detectLocale()
document.documentElement.setAttribute('lang', locale)

const i18n = new VueI18n({
  locale,
  fallbackLocale: 'en',
  messages,
  silentTranslationWarn: true
})

export function setLocale(locale) {
  const normalizedLocale = normalizeLocale(locale)
  if (!normalizedLocale) return

  i18n.locale = normalizedLocale
  localStorage.setItem(localeStorageKey, normalizedLocale)
  document.documentElement.setAttribute('lang', normalizedLocale)
}

export default i18n
